import React from 'react';
import { founderDashboardAPI } from '../../services/api';
import { useFeatureAnalytics } from '../../hooks/useFeatureAnalytics';
import FilterBar from '../../components/feature-analytics/FilterBar';
import KPICard from '../../components/founder-dashboard/KPICard';
import TrendChart from '../../components/founder-dashboard/TrendChart';
import DistributionChart from '../../components/founder-dashboard/DistributionChart';
import DataTable from '../../components/founder-dashboard/DataTable';
import RefreshIndicator from '../../components/founder-dashboard/RefreshIndicator';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

export default function ChatAnalyticsPage() {
  const {
    data, loading, error, showError, lastUpdated, fetchData,
    organizationId, campusId, groupId, handleFilterChange
  } = useFeatureAnalytics(founderDashboardAPI.getChatAnalytics);

  if (showError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-800 mb-4">{error}</p>
        <button onClick={fetchData} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  const { kpis = {}, trends = [], distribution = {}, table = [], notice } = data || {};

  const columns = [
    { key: 'name', header: 'Group Name', sortable: true },
    { key: 'organizationName', header: 'Organization', sortable: true },
    {
      key: 'messages7d',
      header: 'Messages (7d)',
      sortable: true,
      render: (value) => value?.toLocaleString() || '0'
    },
    {
      key: 'senders',
      header: 'Active Senders',
      sortable: true,
      render: (value) => value?.toLocaleString() || '0'
    },
    {
      key: 'mediaRate',
      header: 'Media Rate',
      sortable: true,
      render: (value) => typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : 'N/A'
    }
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ChatBubbleLeftRightIcon className="h-6 w-6 text-green-600" />
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Chat Analytics</h2>
            <p className="text-slate-600 text-sm">Messaging activity, engagement, and media sharing</p>
          </div>
        </div>
        <RefreshIndicator lastUpdated={lastUpdated} onRefresh={fetchData} loading={loading} />
      </div>

      <FilterBar
        organizationId={organizationId}
        campusId={campusId}
        groupId={groupId}
        onFilterChange={handleFilterChange}
      />

      {/* Notice Banner */}
      {notice && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <p className="text-amber-700 text-sm">{notice}</p>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPICard
          title="Adoption Rate"
          value={kpis.adoption || 0}
          format="percent"
          subtitle="Active groups (7d)"
          loading={loading && !data}
        />
        <KPICard
          title="Messages per Day"
          value={kpis.messagesPerDay || 0}
          format="decimal"
          loading={loading && !data}
        />
        <KPICard
          title="Active Senders"
          value={kpis.activeSenders || 0}
          subtitle="7-day window"
          loading={loading && !data}
        />
        <KPICard
          title="Media Share Rate"
          value={kpis.mediaShareRate || 0}
          format="percent"
          loading={loading && !data}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-lg font-medium text-slate-800 mb-4">Message Activity (7 Days)</h3>
          <TrendChart
            data={trends}
            lines={[
              { key: 'messages', name: 'Messages', color: '#22C55E' },
              { key: 'senders', name: 'Active Senders', color: '#3B82F6', yAxisId: 'right' }
            ]}
            type="area"
            height={250}
            rightAxisLabel="Senders"
          />
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-lg font-medium text-slate-800 mb-4">Content Type Distribution</h3>
          <DistributionChart
            data={distribution.contentTypes || {}}
            showPercentage={true}
            height={250}
          />
        </div>
      </div>

      {/* Peak Hours */}
      {distribution.peakHours && Object.keys(distribution.peakHours).length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-8">
          <h3 className="text-lg font-medium text-slate-800 mb-4">Peak Activity Hours (Top 5)</h3>
          <div className="flex justify-around text-center">
            {Object.entries(distribution.peakHours).map(([hour, count]) => (
              <div key={hour} className="px-4 py-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{hour}:00</p>
                <p className="text-xs text-slate-500 mt-1">{count} days active</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Most Active Groups Table */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="text-lg font-medium text-slate-800 mb-4">Most Active Chat Groups</h3>
        <DataTable
          data={table}
          columns={columns}
          loading={loading && !data}
          emptyMessage="No chat activity found"
        />
      </div>
    </div>
  );
}
